/**
 * Typed API client for the azabu backend.
 *
 * Auth: requests carry the Clerk session token as
 * `Authorization: Bearer <token>`. The one exception is `getFeatureGates(null)`:
 * a signed-out caller fetches gates with no `Authorization` header.
 */

import type { CrochetSizingDefaults } from '@/crochet-editor/sizing-defaults';
import type { StrokeStyle } from '@/crochet-editor/stroke-style';

export type Theme = 'light' | 'dark' | 'system';

export interface UserSettings {
    theme: Theme;
    /** Per-stitch-type default sizing (SP-6). Absent/null until the user has
     *  saved one — and absent entirely until the backend column ships. */
    crochetSizingDefaults?: CrochetSizingDefaults | null;
    /** Diagram-wide stroke style for NEW crochet diagrams. Absent/null until
     *  the user has saved one. */
    crochetStrokeDefaults?: StrokeStyle | null;
}

/**
 * Derive the backend origin from the current page's host.
 *
 * - On localhost / 127.0.0.1: use `VITE_API_BASE_URL` (env var) or fall back
 *   to `http://localhost:8787`.
 * - On an editor host — production's `editor.stitchpress.kinnet.studio` or a
 *   prefixed one like `dev.editor.stitchpress.kinnet.studio` — replace the
 *   `editor.` label with `api.`.
 * - Everything else: use the page origin as-is (shouldn't normally occur, but
 *   avoids throwing on unexpected hosts).
 */
export function apiBaseUrl(): string {
    const { hostname, origin } = window.location;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';
    }

    if (hostname.startsWith('editor.') || hostname.includes('.editor.')) {
        return origin.replace('editor.', 'api.');
    }

    return origin;
}

/** GET /api/settings — throws on non-2xx. */
export async function getUserSettings(token: string): Promise<UserSettings> {
    const res = await fetch(`${apiBaseUrl()}/api/settings`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        throw new Error(`GET /api/settings failed: ${res.status}`);
    }

    return res.json() as Promise<UserSettings>;
}

/** PATCH /api/settings — throws on non-2xx. */
export async function updateUserSettings(
    token: string,
    patch: Partial<UserSettings>,
): Promise<UserSettings> {
    const res = await fetch(`${apiBaseUrl()}/api/settings`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(patch),
    });

    if (!res.ok) {
        throw new Error(`PATCH /api/settings failed: ${res.status}`);
    }

    return res.json() as Promise<UserSettings>;
}

/** Authoritative answer to "may this user use paid features". Null once expired. */
export interface BillingEntitlement {
    tier: string;
    source: string;
    /** ISO timestamp, or null for a grant that never expires (founding accounts). */
    expiresAt: string | null;
}

/**
 * Presentation only — survives expiry so the UI can say what happened.
 * Never infer entitlement from this; use `entitlement`.
 */
export interface BillingSubscription {
    status: string;
    renewsAt: string | null;
    endsAt: string | null;
}

/**
 * What the account's plan allows. Read it through `usePlan()` rather than
 * fetching it directly, and never hardcode a limit: `activeDiagramLimit` is
 * the number, and `null` means unlimited.
 */
export interface BillingPlan {
    pro: boolean;
    /** False while the backend's free-plan switch is off, when everyone is treated as Pro for limits. */
    enforced: boolean;
    /** Active (non-archived) diagrams allowed, or null for no limit (Pro, or not enforced). */
    activeDiagramLimit: number | null;
    aiDailyEdits: number;
    /** Base checkout url, or null when Pro can't be bought in this environment. */
    checkoutUrl: string | null;
}

export interface BillingStatus {
    entitlement: BillingEntitlement | null;
    subscription: BillingSubscription | null;
    plan: BillingPlan;
}

/** GET /api/billing — throws on non-2xx. */
export async function getBillingStatus(token: string): Promise<BillingStatus> {
    const res = await fetch(`${apiBaseUrl()}/api/billing`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        throw new Error(`GET /api/billing failed: ${res.status}`);
    }

    return res.json() as Promise<BillingStatus>;
}

/**
 * GET /api/billing/portal — the Lemon Squeezy customer portal (cancel, resume,
 * update card, invoices). Minted on demand because the url is signed and
 * short-lived, so it must not be cached. Throws on non-2xx.
 */
export async function getBillingPortalUrl(token: string): Promise<string> {
    const res = await fetch(`${apiBaseUrl()}/api/billing/portal`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        throw new Error(`GET /api/billing/portal failed: ${res.status}`);
    }

    return (await res.json()).url as string;
}

/**
 * POST /api/billing/cancel or /resume. Cancelling stops renewal (access runs to
 * the end of the paid period); resuming undoes that during the grace period.
 * Both answer with the new billing state, same shape as GET /api/billing.
 * Throws on non-2xx.
 */
async function changeSubscription(token: string, change: 'cancel' | 'resume'): Promise<BillingStatus> {
    const res = await fetch(`${apiBaseUrl()}/api/billing/${change}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        throw new Error(`POST /api/billing/${change} failed: ${res.status}`);
    }

    return res.json() as Promise<BillingStatus>;
}

export function cancelSubscription(token: string): Promise<BillingStatus> {
    return changeSubscription(token, 'cancel');
}

export function resumeSubscription(token: string): Promise<BillingStatus> {
    return changeSubscription(token, 'resume');
}

/** What `POST /api/offline/install-token` returns on success. */
export interface OfflineInstallTokenResponse {
    /** Signed install token: `base64url(claims).base64url(signature)`. */
    token: string;
    /** Expiry, in Unix milliseconds. */
    expiresAt: number;
    /** The offline site the token is valid for (its `aud`). */
    offlineOrigin: string;
}

/** Why an install-token request failed — mapped to copy by the caller. */
export type OfflineTokenErrorReason =
    | 'not_entitled'
    | 'trial_not_eligible'
    | 'rate_limited'
    | 'unavailable'
    | 'failed';

/** Thrown by `requestOfflineInstallToken`; `reason` says which message to show. */
export class OfflineTokenError extends Error {
    constructor(public readonly reason: OfflineTokenErrorReason) {
        super(`offline install token request failed: ${reason}`);
        this.name = 'OfflineTokenError';
    }
}

function isOfflineInstallTokenResponse(body: unknown): body is OfflineInstallTokenResponse {
    if (typeof body !== 'object' || body === null) return false;
    const { token, expiresAt, offlineOrigin } = body as Record<string, unknown>;
    return (
        typeof token === 'string' && token !== '' &&
        typeof expiresAt === 'number' && Number.isFinite(expiresAt) &&
        typeof offlineOrigin === 'string' && offlineOrigin !== ''
    );
}

/**
 * POST /api/offline/install-token — mints a short-lived token that lets the
 * offline site download the build. Throws `OfflineTokenError` on any failure:
 * 403 `not_entitled` / `trial_not_eligible`, 429 `rate_limited`, 503
 * `unavailable`; everything else (another status, a 403 with an unknown or
 * unreadable body, a 2xx body missing a field, a network error) is `failed`.
 */
export async function requestOfflineInstallToken(
    token: string,
): Promise<OfflineInstallTokenResponse> {
    let res: Response;
    try {
        res = await fetch(`${apiBaseUrl()}/api/offline/install-token`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch {
        throw new OfflineTokenError('failed');
    }

    if (res.status === 429) throw new OfflineTokenError('rate_limited');
    if (res.status === 503) throw new OfflineTokenError('unavailable');

    if (res.status === 403) {
        const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
        const error = body?.error;
        if (error === 'not_entitled' || error === 'trial_not_eligible') {
            throw new OfflineTokenError(error);
        }
        throw new OfflineTokenError('failed');
    }

    if (!res.ok) throw new OfflineTokenError('failed');

    const body: unknown = await res.json().catch(() => null);
    if (!isOfflineInstallTokenResponse(body)) throw new OfflineTokenError('failed');
    return body;
}

/**
 * How long GET /api/gates may take, including reading the body, before it is
 * aborted. Without a bound a stalled connection would leave gates 'pending'
 * forever on a load with no cached values. Matches the Statsig SDK's default
 * network timeout, which bounded the gate fetch this request replaced.
 */
export const FEATURE_GATES_TIMEOUT_MS = 10_000;

/**
 * GET /api/gates — every feature gate, evaluated by the backend for the caller.
 * Works signed in (Bearer token) or signed out (no `Authorization` header; the
 * backend evaluates an anonymous caller). Throws on non-2xx, when the body
 * has no `gates` object, or when no answer arrives within `timeoutMs`;
 * entries that aren't booleans are dropped.
 */
export async function getFeatureGates(
    token: string | null,
    timeoutMs: number = FEATURE_GATES_TIMEOUT_MS,
): Promise<Record<string, boolean>> {
    const res = await fetch(`${apiBaseUrl()}/api/gates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: AbortSignal.timeout(timeoutMs),
    });

    if (!res.ok) {
        throw new Error(`GET /api/gates failed: ${res.status}`);
    }

    const body = (await res.json()) as { gates?: unknown } | null;
    const gates = body?.gates;
    if (typeof gates !== 'object' || gates === null || Array.isArray(gates)) {
        throw new Error('GET /api/gates returned no gates');
    }

    const result: Record<string, boolean> = {};
    for (const [name, value] of Object.entries(gates)) {
        if (typeof value === 'boolean') result[name] = value;
    }
    return result;
}
